import dayjs, { Dayjs } from 'dayjs';
import { FixedRule, FixedRuleBase } from 'repository/timekeeper/@types/FixedRuleModel';
import { useFixedRuleActions } from './useFixRuleActions';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { normalizeString } from 'utils/Validations';

interface Props {
    rows: FixedRule[];
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info';
    selectedRow: FixedRule[];
    open: boolean;
    setOpen: (open: boolean) => void;
    cleanSelected: () => void;
}

const EMPTY_FORM: FixedRuleBase = {
    name: '',
    description: '',
    type_payment_period: 'day',
    days: 7,
    day_reference: 1,
    date_reference: dayjs(),
    day_division_hours: dayjs(new Date().setHours(0, 0, 0, 0)),
    hours_belong_to: 'Dia de entrada planificado',
    enable: true,
};

const toFormFromFixedRule = (fixedRule: FixedRule): FixedRuleBase => ({
    name: fixedRule.name,
    description: fixedRule.description ?? '',
    type_payment_period: fixedRule.type_payment_period,
    days: fixedRule.days,
    day_reference: fixedRule.day_reference,
    date_reference: dayjs(fixedRule.date_reference),
    day_division_hours: dayjs(fixedRule.day_division_hours, 'HH:mm:ss'),
    hours_belong_to: fixedRule.hours_belong_to,
    enable: fixedRule.enable,
});

export const useFixRuleForm = ({ rows, reloading, mode, selectedRow, open, setOpen, cleanSelected }: Props) => {
    const currentFixedRule = mode === 'edit' || mode === 'info' ? selectedRow[0] : undefined;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { addFixedRule, updateFixedRule, deleteFixedRules } = useFixedRuleActions();
    const [initialForm, setInitialForm] = useState<FixedRuleBase>(EMPTY_FORM);
    const [form, setForm] = useState<FixedRuleBase>(EMPTY_FORM);
    const [alert, setAlert] = useState(false);

    useEffect(() => {
        if (!open) return;

        if ((mode === 'edit' || mode === 'info') && currentFixedRule) {
            const preloadedForm = toFormFromFixedRule(currentFixedRule);
            setForm(preloadedForm);
            setInitialForm(preloadedForm);
            return;
        }
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
    }, [open, mode, currentFixedRule]);

    const update = <K extends keyof FixedRuleBase>(field: K, value: FixedRuleBase[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const existName = useMemo(() => {
        return (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => {
                if (mode === 'edit' && currentFixedRule && row.fixed_rule_id === currentFixedRule.fixed_rule_id) return false;
                return normalizeString(row.name) === normalizedInput;
            });
        };
    }, [rows, mode, currentFixedRule]);

    const isValidMomentField = (momentValue: Dayjs | null) => {
        return !momentValue || !momentValue.isValid;
    };

    const validateDaysReference = useCallback(
        (value: number) => {
            if (form.type_payment_period === 'bimonthly') {
                return value >= 1 && value <= 15;
            }

            if (form.type_payment_period === 'monthly') {
                return value >= 1 && value <= 31;
            }

            return true;
        },
        [form.type_payment_period],
    );

    const disabled = useMemo(() => {
        return (
            form.name === '' ||
            form.type_payment_period === '' ||
            form.day_division_hours === null ||
            !validateDaysReference(form.day_reference) ||
            form.date_reference === null ||
            isValidMomentField(form.day_division_hours) ||
            form.hours_belong_to === ''
        );
    }, [form, validateDaysReference]);

    const cleanForm = () => {
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
        setAlert(false);
        cleanSelected();
        setOpen(false);
    };

    const hasChanges = useMemo(() => {
        return JSON.stringify(form) !== JSON.stringify(initialForm);
    }, [form, initialForm]);

    const detectChanges = () => {
        if (hasChanges) {
            setAlert(true);
        } else {
            cleanForm();
        }
    };

    const handleSubmit = () => {
        const body = {
            ...form,
            date_reference: form.date_reference ? dayjs(form.date_reference).format('YYYY-MM-DD') : '',
            day_division_hours: form.day_division_hours ? dayjs(form.day_division_hours).format('HH:mm') : '',
        } as unknown as FixedRuleBase;

        if (mode === 'edit' && currentFixedRule) {
            updateFixedRule({
                setLoading: () => {},
                fixedRuleId: currentFixedRule.fixed_rule_id,
                body,
                closing: cleanForm,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.fixed_rules.dialog.alerts.edit.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.fixed_rules.dialog.alerts.edit.success'),

                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
            return;
        }

        if (mode === 'add') {
            addFixedRule({
                setLoading: () => {},
                body,
                closing: cleanForm,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.fixed_rules.dialog.alerts.add.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.fixed_rules.dialog.alerts.add.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }

        if (mode === 'delete') {
            deleteFixedRules({
                setLoading: () => {},
                fixedRuleIds: selectedRow.map((row) => row.fixed_rule_id),
                closing: cleanForm,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.fixed_rules.dialog.alerts.delete.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.fixed_rules.dialog.alerts.delete.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }
    };

    return {
        form,
        alert,
        update,
        disabled,
        setAlert,
        existName,
        cleanForm,
        handleSubmit,
        detectChanges,
        validateDaysReference,
    };
};
